import Vue from 'utils/vueUtils';
import User from '../../store/models/User';
import { sortBy } from 'lodash';
import getInboxType from '../../utils/getInboxType';
import { baseApi } from 'core/api';
import {
    useAutoRechargeStore,
    useBusinessHoursStore,
    useClientInvitationsStore,
    useClientsStore,
    useInboxesStore,
    useInvitationsStore,
    useNumberStore,
    useOrganizationStore,
    useSubscriptionsStore,
    useToastStore,
} from 'store/modules';

export const TYPES_INBOX = {
    PERSONAL: 1,
    SHARED: 2,
    UNIFIED: 3,
    GROUP: 4,
    SMART: 5,
};

export const USER_ROLES = {
    VIEW_ONLY: 'view_only',
    MEMBER: 'member',
    MANAGER: 'manager',
    ADMIN: 'admin',
    OWNER: 'owner',
};

export const USER_ROLES_LIST = [
    {
        role: USER_ROLES.VIEW_ONLY,
        label: 'Observer',
    },
    {
        role: USER_ROLES.MEMBER,
        label: 'Member',
    },
    {
        role: USER_ROLES.MANAGER,
        label: 'Manager',
    },
    {
        role: USER_ROLES.ADMIN,
        label: 'Admin',
    },
];

export const USER_ROLES_LIST_FOR_TRIAL = [
    {
        role: USER_ROLES.VIEW_ONLY,
        label: 'Observer',
    },
];

const defaultUser = () => ({
    id: 0,
    firstName: '',
    lastName: '',
    fullName: '',
    email: '',
    currentInboxId: '',
    currentInboxTypeId: '',
    currentInboxType: '',
    photoUrl: '',
    createdAt: '',
    timezone: '',
    country: '',
    contact_fields: null,
    isRecordAutomatically: false,
    isRingEverywhere: false,
    isAway: false,
    awayStatusLastDate: null,
    isShowTenDLCBanner: false,
    tenDLCBannerLastDate: null,
});

const formatUser = (user) => ({
    id: user.id,
    firstName: user.firstName || user.first_name,
    lastName: user.lastName || user.last_name,
    fullName: user.fullName || user.full_name,
    email: user.email,
    number: user.formatted_number || user.number,
    photoUrl: user.photoUrl || user.photo_url,
    role: user.role,
    createdAt: user.createdAt || user.created_at,
    currentInboxId: user.currentInboxId,
    currentInboxTypeId: user.currentInboxTypeId,
    currentInboxType: user.currentInboxType,
    timezone: user.timezone,
    defaultTeamId: user.defaultTeamId,
    contact_fields: user.contact_fields,
    isGroupsFlow: user.isGroupsFlow || false,
    hasAccessibleOrganizations: user.hasAccessibleOrganizations || false,
    isRequireNumber: Boolean(user.isRequireNumber),
    isRequirePftNumber: Boolean(user.isRequirePftNumber),
    isPftTrialFlow: Boolean(user.isPftTrialFlow),
    isRecordAutomatically: Boolean(user.isRecordAutomatically),
    isRingEverywhere:
        user.isRingEverywhere !== undefined ? Boolean(user.isRingEverywhere) : undefined,
    isAway: Boolean(user.isAway),
    isShowTenDLCBanner: Boolean(user.dlcBannerShouldShowAgain),
    awayStatusLastDate: user.awayStatusLastDate || false,
    tenDLCBannerLastDate: user.dlcBannerShowLastDate || null,
    inboxesCount: user.inboxesCount || 0,
    country: user.country || 'US',
});

const state = () => ({
    _appToken: null,
    _user: null,
    _settings: [],
    _permissions: {},
    _social: null,
    _developer: false,
    _loggedOut: false,
    _loggingOut: false,
    _csrfToken: '',
    _integrationDisconnected: null,
    _contact_fields: null,
    _publicApiClient: false,
    _loading: true,
    _loadingApp: true,
    _showModalChangeRoleRequest: false,
    _customModalChangeRoleText: null,
    _isRecordAutomatically: false,
    _isRingEverywhere: false,
    _config: null,
    _tokens: [],
    _tokensLoading: false,
    _isReadyNewDesign: false,
    _multiorgUserData: {
        email: null,
    },
});

const getters = {
    user: (state) => state._user,
    isMemberRole: (state) => state._user?.role === USER_ROLES.MEMBER,
    isManagerRole: (state) => state._user?.role === USER_ROLES.MANAGER,
    userSettings: (state) => state._settings,
    isLoggedIn: (state) => Boolean(state._user?.isLoggedIn),
    userPermissions: (state) => state._permissions,
    socialUser: (state) => state._social,
    isViewOnlyRole: (state) => state._user?.role === USER_ROLES.VIEW_ONLY,
    isDeveloper: (state) => state._developer,
    isLoggedOut: (state) => state._loggedOut,
    csrfToken: (state) => state._csrfToken,
    integrationDisconnected: (state) => state._integrationDisconnected,
    contactActiveFieldsColumn: (state) => {
        if (!state._contact_fields) {
            return ['name', 'number', 'email', 'tags', 'created_at'];
        }

        return state._contact_fields.active_fields;
    },
    isPublicApiClient: (state) => state._publicApiClient,
    loadingMain: (state) => state._loading,
    loadingApp: (state) => state._loadingApp,
    showModalChangeRoleRequest: (state) => state._showModalChangeRoleRequest,
    customModalChangeRoleText: (state) => state._customModalChangeRoleText,
    isRecordAutomatically: (state) => state._isRecordAutomatically,
    isRingEverywhere: (state) => state._isRingEverywhere,
    config: (state) => state._config,
    tokens: (state) => sortBy(state._tokens, 'created_at').reverse(),
    tokensLoading: (state) => state._tokensLoading,
    isAway: (state) => Boolean(state._user?.isAway),
    multiorgUserData: (state) => state._multiorgUserData,
    appToken: (state) => state._appToken,
    hasOid: (state) => {
        if (!state._appToken) return null;

        try {
            const [, tokenData] = state._appToken.split('.');
            const parsedData = JSON.parse(atob(tokenData));

            return Boolean(parsedData.oid);
        } catch (e) {
            console.error(e);
            return false;
        }
    },
};

const utils = {
    settingFlag: (key, userSettings, organizationSettings) => {
        const userFlag =
            !!userSettings &&
            userSettings.find((option) => option.key === key) &&
            userSettings.find((option) => option.key === key).settings.active;

        const organizationFlag =
            !!organizationSettings &&
            organizationSettings.find((option) => option.key === key) &&
            organizationSettings.find((option) => option.key === key).settings.active;

        return Boolean(userFlag || organizationFlag);
    },
};

const actions = {
    setLoadingMain(status = false) {
        this._setUserLoadingMain(status);
    },

    settingFlag(key = '') {
        const { organizationSettings } = useOrganizationStore();

        return utils.settingFlag(key, this.userSettings, organizationSettings);
    },

    loggingOut() {
        this._setUserLoggingOut();
    },

    loggedOut() {
        if (this._loggingOut) {
            return;
        }

        this._setUserLoggedOut();
    },

    updateAuthReset(form) {
        return form.post('core/auth/reset').then((response) => {
            return response;
        });
    },

    setUserData(data) {
        if (data.user.id) {
            useOrganizationStore()._setOrganization(data.organization);
            useBusinessHoursStore()._setBusinessHoursLoaded(data.businessHours);
            useAutoRechargeStore()._setAutoRechargeLoaded(data.autorecharge);
            useOrganizationStore()._setUserFeatureFlagsLoaded(data.feature_flags);
            this._setUsersSettings(data.userSettings);
            useOrganizationStore()._setOrganizationSettingsLoaded(data.organizationSettings);
            this._setUsersConfig(data.config);
            this._setUserPermissions(data.userPermissions);
            this._setUserOwnerLoaded(data.user);
            useNumberStore()._setOrganizationNumberLoaded(data.number);
            this._setUserPublicApiClient({
                organizationSettings: data.organizationSettings,
                userSettings: data.userSettings,
            });
            useInboxesStore()._setInboxesLoaded({
                inboxes: data.inboxes,
                inboxesGroups: data.inboxesGroups,
                unifiedInbox: data.unifiedInbox,
            });
            useNumberStore()._setNumberVendorIntegrations(data.numberVendorIntegrations);
            this._setUserMeSignal();
        } else {
            this.resetStateUser();
        }
    },

    setSocialUserData(data) {
        this._setUserSocialAuthenticated(data);
        this.resetStateUser();
    },

    getUsersProfile({ email }) {
        return baseApi
            .get('core/users/profile', {
                params: {
                    email,
                },
            })
            .then((response) => {
                return response;
            });
    },

    updateUsersProfile(form) {
        return form.post('core/users/profile').then((response) => {
            this._setUserProfileUpdated(response.data);

            return response;
        });
    },

    updateUsersProfileNumber({ params }) {
        return baseApi.post('core/users/profile/number', params).then((response) => {
            this._setUserUpdateState({
                props: {
                    number: response.data.formatted_number,
                },
                field: 'user',
            });

            return response;
        });
    },

    mergeUserEmail(form) {
        return form.post('core/auth/merge-and-login').then((response) => {
            return response.data;
        });
    },

    updateUsersEmail(form) {
        return form.post('core/users/email').then((response) => {
            if (response.data?.need_merge) {
                return response.data;
            }

            this._setUserProfileUpdated(response.data);

            return response;
        });
    },

    updateUsersPassword(form) {
        return form.post('core/users/password').then((response) => {
            this._setUserProfileUpdated(response.data);

            return response;
        });
    },

    updateUsersChangeRoleRequest(form) {
        return form.post('core/users/change-role-request').then((response) => {
            return response;
        });
    },

    updateUsersAssignInboxRequest(form) {
        return form.post('core/users/assign-inbox-request').then((response) => {
            return response;
        });
    },

    getUsersAssignInboxRequest() {
        return baseApi.get('core/users/assign-inbox-request').then((response) => {
            return response.data;
        });
    },

    updateUsersSettings(form) {
        return form.patch('core/users/settings').then((response) => {
            this._setUserSettingsUpdated(response.data);

            return response;
        });
    },

    updateUsersSettingsMultiple(form) {
        return form.patch('core/users/settings-multiple').then((response) => {
            for (let feature of response.data) {
                this._setUserSettingsUpdated(feature);
            }
            return response;
        });
    },

    removeIntegrationDisconnected() {
        this._setUserIntegrationDisconnectedCleared();
    },

    setCurrentInboxType(inboxType) {
        this._setSetCurrentInboxType(inboxType);
    },

    updateUsersTeam(id) {
        const params = {
            id: id || 0,
            type: getInboxType(id),
        };

        return baseApi.put('core/users/team', params).then((response) => {
            this._setUpdateInboxId(response.data);
            return response.data;
        });
    },

    getAuthToken() {
        return baseApi.get('core/auth/token').then((response) => {
            return response.data;
        });
    },

    getAuthTokens() {
        this._setUserTokensLoading(true);

        return baseApi
            .get('core/auth/tokens')
            .then((response) => {
                this._setUserTokensLoad(response.data);

                return response.data;
            })
            .finally(() => {
                this._setUserTokensLoading(false);
            });
    },

    deleteAuthTokensById({ form, id }) {
        return form.delete(`core/auth/tokens/${id}`).then((response) => {
            this._setUserTokensDelete(id);

            return response.data;
        });
    },

    getOauthToken(params) {
        return baseApi.get('core/oauth/token', { params }).then((response) => {
            return response.data;
        });
    },

    updateAuthLogout() {
        window.localStorage.setItem('isAwayCounter', 0);
        window.localStorage.setItem('isAwayActions', 0);
        return baseApi
            .post('core/auth/logout', null, { withCredentials: true })
            .then((response) => {
                return response.data;
            });
    },

    updateAuthForgot({ form }) {
        return form.post('core/auth/forgot').then((response) => {
            return response.data;
        });
    },

    updateAuthForgotForce() {
        return baseApi.post('core/auth/forgot/force').then((response) => {
            return response.data;
        });
    },

    updateAuthOauth(data) {
        return baseApi.post('core/auth/oauth', data).then((response) => {
            return response.data;
        });
    },

    deleteAuthLogout() {
        window.localStorage.setItem('isAwayCounter', 0);
        window.localStorage.setItem('isAwayActions', 0);
        return baseApi.delete('core/auth/logout', { withCredentials: true }).then((response) => {
            return response.data;
        });
    },

    updateAuthClients({ form }) {
        return form.post('core/auth/clients').then((response) => {
            return response.data;
        });
    },

    updateAuthClientsById({ form, id }) {
        return form.put(`core/auth/clients/${id}`).then((response) => {
            return response.data;
        });
    },

    deleteAuthClientsById({ form, id }) {
        return form.delete(`core/auth/clients/${id}`).then((response) => {
            return response.data;
        });
    },

    getAuthClients() {
        return baseApi.get('core/auth/clients').then((response) => {
            return response.data;
        });
    },

    getAuthPersonalAccessTokens() {
        return baseApi.get('core/auth/personal-access-tokens').then((response) => {
            return response.data;
        });
    },

    updateAuthPersonalAccessTokens({ form }) {
        return form.post('core/auth/personal-access-tokens').then((response) => {
            return response.data;
        });
    },

    deleteAuthPersonalAccessTokensById({ form, id }) {
        return form.delete(`core/auth/personal-access-tokens/${id}`).then((response) => {
            return response.data;
        });
    },

    getAuthAgencyById({ id }) {
        return baseApi.get(`core/auth/agency/${id}`).then((response) => {
            return response.data;
        });
    },

    updateAuthAgency({ form }) {
        return form.post('core/auth/agency').then((response) => {
            return response.data;
        });
    },

    getAuthScopes() {
        return baseApi.get('core/auth/scopes').then((response) => {
            return response.data;
        });
    },

    getUserMe({ params = {}, update = true } = {}) {
        return baseApi.get('core/users/me', { params }).then(async (response) => {
            const data = response.data.data;
            window.localStorage.setItem(
                'isAwayCounter',
                +window.localStorage.getItem('isAwayCounter') + 1
            );
            window.localStorage.setItem('isAway', data.user?.isAway);
            window.localStorage.setItem('awayStatusLastDate', data.user?.awayStatusLastDate);

            const organizationSettings = data?.organizationSettings;
            const tenDLCSettings = organizationSettings?.find(
                (setting) => setting?.key === '10dlc_banners'
            )?.settings['active'];

            if (data.config?.['10dlc_banners_info']?.should_show_banner && tenDLCSettings) {
                if (data.user.id)
                    window.localStorage.setItem(`${data.user.id}_isShowTenDLCBanner`, true);
            } else {
                if (data.user.id)
                    window.localStorage.setItem(`${data.user.id}_isShowTenDLCBanner`, false);
            }

            if (update) {
                if (data.user.id) {
                    data.subscription = await useSubscriptionsStore().getSubscription();

                    useOrganizationStore()._setOrganization(data.organization);
                    useBusinessHoursStore()._setBusinessHoursLoaded(data.businessHours);
                    useAutoRechargeStore()._setAutoRechargeLoaded(data.autorecharge);
                    useOrganizationStore()._setUserFeatureFlagsLoaded(data.feature_flags);
                    this._setUsersSettings(data.userSettings);
                    useOrganizationStore()._setOrganizationSettingsLoaded(
                        data.organizationSettings
                    );
                    this._setUsersConfig(data.config);
                    this._setUserPermissions(data.userPermissions);
                    this._setUserOwnerLoaded(data.user);
                    useNumberStore()._setOrganizationNumberLoaded(data.number);
                    this._setUserPublicApiClient({
                        organizationSettings: data.organizationSettings,
                        userSettings: data.userSettings,
                    });
                    useInboxesStore()._setInboxesLoaded({
                        inboxes: data.inboxes,
                        inboxesGroups: data.inboxesGroups,
                        unifiedInbox: data.unifiedInbox,
                    });
                    useNumberStore()._setNumberVendorIntegrations(data.numberVendorIntegrations);
                    this._setUserMeSignal();
                } else {
                    this.resetStateUser();
                }
            }

            return response.data;
        });
    },

    getUserOwner() {
        return baseApi.get('core/user').then((response) => {
            const user = response.data;

            window.localStorage.setItem(
                'isAwayCounter',
                +window.localStorage.getItem('isAwayCounter') + 1
            );
            window.localStorage.setItem('isAway', user?.isAway);
            window.localStorage.setItem('awayStatusLastDate', user?.awayStatusLastDate);

            this._setUserOwnerLoaded(response.data);

            return response.data;
        });
    },

    getUsersPermissions() {
        return baseApi.get('core/users/permissions').then((response) => {
            this._setUserPermissions(response.data);

            return response.data;
        });
    },

    getUsersConfig() {
        return baseApi.get('core/users/config').then((response) => {
            this._setUsersConfig(response.data);

            return response.data;
        });
    },

    _setUsersConfig(config) {
        this._config = config;
        this._developer = config.developer;
        this._csrfToken = config.csrfToken;
        this._integrationDisconnected = config.integrationDisconnected;
    },

    getUsersSettings() {
        return baseApi.get('core/users/settings').then((response) => {
            const usersSettings = response.data;

            this._setUsersSettings(usersSettings);

            return usersSettings;
        });
    },

    updateAwayStatus({ status }) {
        return baseApi.post('core/users/away-status', { away_status: status }).then((response) => {
            return response.data;
        });
    },

    updateTenDLCBannerDate() {
        return baseApi
            .post('core/users/dlc-banner-showed', { banner_showed: true })
            .then((response) => {
                return response.data;
            });
    },

    sendHubspotAnswers({ answers }) {
        if (!answers) return;

        const reqAnswers = {
            email: answers.email,
            country:
                answers.country.value === 'world'
                    ? answers.country.option?.label
                    : answers.country.label,
            team_size: answers.companySize?.value,
            integration: answers.crm?.value,
        };

        if (answers.crm?.value === 'Other') {
            reqAnswers.integration_sync__typeform = answers.crm?.customCRMName;
        }

        return baseApi.post('core/hubspot/answers', reqAnswers).then((response) => {
            return response;
        });
    },

    async loadCountries() {
        return baseApi.get('core/countries').then((response) => {
            return response;
        });
    },

    setAwayStatus({ data }) {
        window.localStorage.setItem('isAway', data.isAway);
        window.localStorage.setItem('awayStatusLastDate', data.awayStatusLastDate);

        if (!data.isAway && window.AwayStatusModal) {
            clearTimeout(window.AwayStatusModal.timer);
            window.AwayStatusModal.timer = null;
            window.AwayStatusModal.isShow = false;
        }

        this._setUserUpdateAwayStatus(data);
    },

    updateAuthRefresh(enabledChangeDomainApi = false) {
        const assumeToken = sessionStorage.getItem('assume_token');
        const config = assumeToken
            ? undefined
            : {
                  withCredentials: true,
              };

        return baseApi
            .post('core/auth/refresh', null, {
                ...config,
                enabledChangeDomainApi: enabledChangeDomainApi,
            })
            .then((response) => {
                return response.data;
            });
    },

    updateUsersSmsCode({ form, number }) {
        return form.post('core/users/sms-code', { number }).then((response) => {
            return response.data;
        });
    },

    changeModalChangeRoleRequest({ status, customText }) {
        this._setUserModalChangeRoleRequest({ status, customText });
    },

    updateUsersToggleRecordAutomatically(bool) {
        return baseApi
            .post('core/users/toggle-record-automatically', {
                isRecordAutomatically: bool,
            })
            .then((response) => {
                this._setUserUpdateRecordAutomatically(bool);
            });
    },

    updateUsersToggleRingEverywhere(bool) {
        return baseApi
            .post('core/users/toggle-ring-everywhere', {
                isRingEverywhere: bool,
            })
            .then((response) => {
                this._setUserUpdateRingEverywhere(bool);
            })
            .catch((error) => {
                return error.response;
            });
    },

    recordAutomaticallyFromSocket(bool) {
        this._setUserUpdateRecordAutomatically(bool);
    },

    ringEverywhereFromSocket(bool) {
        this._setUserUpdateRingEverywhere(bool);
    },

    updateUsersPush(data) {
        return baseApi.post('core/users/push', data).then((response) => {
            return response.data;
        });
    },

    deleteUsersPush(params) {
        return baseApi.delete('core/users/push', { params }).then((response) => {
            return response.data;
        });
    },

    updateUsersRegisterAccept({ form, update = true }) {
        return form.post('core/auth/register/accept').then((response) => {
            const data = response.data.data;

            if (update) {
                if (data.user.id) {
                    useOrganizationStore()._setOrganization(data.organization);
                    useBusinessHoursStore()._setBusinessHoursLoaded(data.businessHours);
                    useAutoRechargeStore()._setAutoRechargeLoaded(data.autorecharge);
                    useOrganizationStore()._setUserFeatureFlagsLoaded(data.feature_flags);
                    this._setUsersSettings(data.userSettings);
                    useOrganizationStore()._setOrganizationSettingsLoaded(
                        data.organizationSettings
                    );
                    this._setUsersConfig(data.config);
                    this._setUserPermissions(data.userPermissions);
                    this._setUserOwnerLoaded(data.user);
                    useNumberStore()._setOrganizationNumberLoaded(data.number);
                    this._setUserPublicApiClient({
                        organizationSettings: data.organizationSettings,
                        userSettings: data.userSettings,
                    });
                    useInboxesStore()._setInboxesLoaded({
                        inboxes: data.inboxes,
                        inboxesGroups: data.inboxesGroups,
                        unifiedInbox: data.unifiedInbox,
                    });
                    useNumberStore()._setNumberVendorIntegrations(data.numberVendorIntegrations);
                    this._setUserMeSignal();
                } else {
                    this.resetStateUser();
                }
            }

            return response.data;
        });
    },

    updateUsersRegisterDirectVerify(data) {
        return baseApi.post('core/users/register/direct/verify', data).then((response) => {
            return response.data;
        });
    },

    updateUsersRegisterVerify(data) {
        return baseApi.post('core/users/register/verify', data).then((response) => {
            return response.data;
        });
    },

    updateAuthRegister({ form, update = true }) {
        return form
            .post('core/auth/register', null, {
                withCredentials: true,
            })
            .then((response) => {
                const data = response.data.data;

                if (update) {
                    if (data.user.id) {
                        useOrganizationStore()._setOrganization(data.organization);
                        useBusinessHoursStore()._setBusinessHoursLoaded(data.businessHours);
                        useAutoRechargeStore()._setAutoRechargeLoaded(data.autorecharge);
                        useOrganizationStore()._setUserFeatureFlagsLoaded(data.feature_flags);
                        this._setUsersSettings(data.userSettings);
                        useOrganizationStore()._setOrganizationSettingsLoaded(
                            data.organizationSettings
                        );
                        this._setUsersConfig(data.config);
                        this._setUserPermissions(data.userPermissions);
                        this._setUserOwnerLoaded(data.user);
                        useNumberStore()._setOrganizationNumberLoaded(data.number);
                        this._setUserPublicApiClient({
                            organizationSettings: data.organizationSettings,
                            userSettings: data.userSettings,
                        });
                        useInboxesStore()._setInboxesLoaded({
                            inboxes: data.inboxes,
                            inboxesGroups: data.inboxesGroups,
                            unifiedInbox: data.unifiedInbox,
                        });
                        useNumberStore()._setNumberVendorIntegrations(
                            data.numberVendorIntegrations
                        );
                        this._setUserMeSignal();
                    } else {
                        this.resetStateUser();
                    }
                }

                return response.data;
            });
    },

    updateAuthRegisterAgency({ form, update = true }) {
        return form.post('core/auth/register/agency').then((response) => {
            const data = response.data.data;

            if (update) {
                if (data.user.id) {
                    useOrganizationStore()._setOrganization(data.organization);
                    useBusinessHoursStore()._setBusinessHoursLoaded(data.businessHours);
                    useAutoRechargeStore()._setAutoRechargeLoaded(data.autorecharge);
                    useOrganizationStore()._setUserFeatureFlagsLoaded(data.feature_flags);
                    this._setUsersSettings(data.userSettings);
                    useOrganizationStore()._setOrganizationSettingsLoaded(
                        data.organizationSettings
                    );
                    this._setUsersConfig(data.config);
                    this._setUserPermissions(data.userPermissions);
                    this._setUserOwnerLoaded(data.user);
                    useNumberStore()._setOrganizationNumberLoaded(data.number);
                    this._setUserPublicApiClient({
                        organizationSettings: data.organizationSettings,
                        userSettings: data.userSettings,
                    });
                    useInboxesStore()._setInboxesLoaded({
                        inboxes: data.inboxes,
                        inboxesGroups: data.inboxesGroups,
                        unifiedInbox: data.unifiedInbox,
                    });
                    useNumberStore()._setNumberVendorIntegrations(data.numberVendorIntegrations);
                    this._setUserMeSignal();
                } else {
                    this.resetStateUser();
                }
            }

            return response.data;
        });
    },

    updateAuthRegisterSecondary({ form, update = true }) {
        return form
            .post('core/auth/register/secondary')
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
    },

    logAuthMetadata(params) {
        const searchParams = new URLSearchParams(window.location.search);
        const coupon = searchParams.get('c') || null;

        const data = {
            coupon_applied: coupon ? 'yes' : 'no',
            ...params,
        };

        return baseApi.post('profile/metadata', data).then((response) => {
            return response.data;
        });
    },

    updateAuthRegisterSecondaryByProfile({ form, update = true }) {
        return form
            .post('core/auth/register/secondary-by-profile')
            .then((response) => {
                return response;
            })
            .catch((error) => {
                return error.response;
            });
    },

    updateUsersFeedback({ form }) {
        return form.post('core/users/feedback').then((response) => {
            return response.data;
        });
    },

    getPublicAuthClients() {
        return baseApi.get('core/public/auth/clients').then((response) => {
            return response.data;
        });
    },

    createPublicAuthClients() {
        return baseApi.post('core/public/auth/clients').then((response) => {
            return response.data;
        });
    },

    deletePublicAuthClientsById({ form, id }) {
        return form.delete(`core/public/auth/clients/${id}`).then((response) => {
            return response.data;
        });
    },

    resetStateUser() {
        this._user = new User(defaultUser());
        this._csrfToken = '';
    },

    updateStateUser({ props }) {
        this._setUserFieldUpdateState({
            props,
        });
    },

    getTwilioData({ account_sid, installation_sid }) {
        return baseApi
            .get('core/addons/twilio', {
                params: {
                    account_sid,
                    installation_sid,
                },
            })
            .then((response) => response.data);
    },

    updateAppLoading(status) {
        this._loadingApp = status;
    },

    switch(data) {
        return baseApi
            .post('core/auth/switch', data)
            .then((response) => {
                if (response.data?.error) {
                    useToastStore().showToast({
                        type: 'danger',
                        message: response.data.error,
                    });
                }

                return response.data;
            })
            .catch((error) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: error.response?.data?.message,
                });

                return error.response;
            });
    },

    updateMultiorgUserData({ props }) {
        this._setUpdateMultiorgUserState({
            props,
        });
    },

    setAppToken(data) {
        this._appToken = data;
    },

    _setUserPermissions(userPermissions) {
        this._permissions = userPermissions || {};
    },

    _setUserOwnerLoaded(user) {
        const currentIsRingEverywhere = this._isRingEverywhere;
        const formattedUser = formatUser(user);

        if (formattedUser.isRingEverywhere === undefined && currentIsRingEverywhere !== undefined) {
            formattedUser.isRingEverywhere = currentIsRingEverywhere;
        }

        user = new User(formattedUser);
        this._user = user;
        this._contact_fields = user.contact_fields;
        this._isRecordAutomatically = user.isRecordAutomatically;
        this._isRingEverywhere = user.isRingEverywhere;

        useInboxesStore()._setUserOwnerLoaded(user);
    },

    _setUsersSettings(data) {
        this._settings = data || [];
    },

    _setUpdateInboxId(data) {
        this._user.currentInboxId = data.currentInboxId;
        this._user.currentInboxTypeId = data.currentInboxTypeId;
        this._user.currentInboxType = data.currentInboxType;

        useInboxesStore()._setUpdateInboxId(data);
    },

    _setInboxMakeDefaultOwner({ data }) {
        this._user.defaultTeamId = data.default_team_id;
    },

    _setUserUpdateState({ props = {}, field = '' }) {
        for (let key in props) {
            if (field) {
                this[`_${field}`][key] = props[key];
            } else {
                this[`_${key}`] = props[key];
            }
        }
    },

    _setUserFieldUpdateState({ props = {} }) {
        for (let key in props) {
            this._user[key] = props[key];
        }

        this._user = new User(formatUser(state._user));
    },

    _setUserTokensLoading(status) {
        this._tokensLoading = status;
    },

    _setUserTokensLoad(tokens) {
        this._tokens = Array.isArray(tokens) ? tokens : [];
    },

    _setUserTokensDelete(id) {
        this._tokens = state._tokens.filter((item) => item.id !== id);
    },

    _setUserUpdateAwayStatus(data) {
        this._user.isAway = data.isAway;
        this._user.awayStatusLastDate = data.awayStatusLastDate;
    },

    _setUserModalChangeRoleRequest({ status, customText }) {
        this._showModalChangeRoleRequest = status;
        this._customModalChangeRoleText = customText || null;
    },

    _setUserLoadingMain(status) {
        this._loading = status;
    },

    _setContactsFilterColumnUpdate(data) {
        this._contact_fields = data.contact_fields;
        this._user.contact_fields = data.contact_fields;
    },

    _setSetCurrentInboxType(currentInboxType) {
        this._currentInboxType = currentInboxType;
    },

    _setUserPublicApiClient({ organizationSettings, userSettings }) {
        this._publicApiClient = utils.settingFlag(
            'public-api-client',
            userSettings,
            organizationSettings
        );
    },

    _setUserSocialAuthenticated(data) {
        this._social = data;
    },

    _setUserLoggingOut() {
        localStorage.removeItem('showedAwayStatusDate');
        this._loggingOut = true;
    },

    _setUserLoggedOut() {
        this._loggedOut = true;
    },

    _setUserProfileUpdated(data) {
        this._user = new User(formatUser(data.profile));
    },

    _setUserSettingsUpdated({ featureKey, settingsKey, value }) {
        if (this._settings.find((o) => o.key === featureKey)) {
            this._settings = this._settings.map((feature) => {
                if (feature.key === featureKey) {
                    if (feature.settings.hasOwnProperty(settingsKey)) {
                        feature.settings[settingsKey] = value;
                    } else {
                        feature.settings = { ...feature.settings };

                        Vue.set(feature.settings, settingsKey, value);
                    }
                }

                return feature;
            });
        } else {
            let feature = {
                key: featureKey,
                settings: {},
            };

            Vue.set(feature.settings, settingsKey, value);

            this._settings.push(feature);
        }
    },

    _setUserIntegrationDisconnectedCleared() {
        this._integrationDisconnected = null;
    },

    _setUserIntegrationDisconnectedSet() {
        this._integrationDisconnected = 1;
    },

    _setUserUpdateRecordAutomatically(data) {
        this._isRecordAutomatically = data;

        if (this._user) {
            this._user.isRecordAutomatically = data;
        }
    },

    _setUserUpdateRingEverywhere(data) {
        this._isRingEverywhere = data;

        if (this._user) {
            this._user.isRingEverywhere = data;
        }
    },

    _setUpdateMultiorgUserState({ props = {} }) {
        for (let key in props) {
            this._multiorgUserData[key] = props[key];
        }
    },

    _setUserMeSignal() {
        useClientInvitationsStore()._setUserMeSignal();
        useInvitationsStore()._setUserMeSignal();
        useClientsStore()._setUserMeSignal();
    },
};

export default {
    state,
    getters,
    actions,
};
